<template>
	<footer ref="footer" class="liquido-footer">
		<!-- Render the info area when EITHER the infoText prop is set OR a parent filled the
		     #info slot. Guarding on the prop alone silently swallowed slot content: poll-show.vue
		     has been passing an #info slot without an infoText prop, so its message never showed. -->
		<div v-if="infoText || $slots.info" class="liquido-footer-info">
			<slot name="info">
				<span v-if="infoText !== undefined">{{ infoText }}</span>
			</slot>
		</div>
		<div class="liquido-footer-actions" :class="{ 'no-nav-icons': hideNavIcons }">
			<slot name="left">
				<RouterLink v-if="!hideNavIcons" to="/team" class="footer-icon-container" aria-label="Team">
					<div class="footer-icon"><team-icon /></div>
					<div class="footer-icon-title">Team</div>
				</RouterLink>
				<!-- Empty, but still a .footer-icon-container: it keeps its flex: 1 1 0 share, so the
				     centered action button stays exactly as wide as it is on every other page. -->
				<div v-else class="footer-icon-container" aria-hidden="true"></div>
			</slot>
			<div class="footer-center">
				<slot name="primary">
					<button v-if="primaryText !== undefined" class="btn" type="button" :disabled="primaryDisabled"
						@click="$emit('primary')">
						{{ primaryText }}
					</button>
				</slot>
			</div>
			<div v-if="!hideNavIcons" class="footer-icon-container" @click="$root.gotoPolls">
				<!-- div class="footer-icon"><i class="fas fa-balance-scale"></i></div> -->
				<!-- LIQUIDO Balance-scale icon for polls -->
				<svg xmlns="http://www.w3.org/2000/svg" 
					class="footer-svg-icon" 
				  viewBox="0 5 130 90"
					fill="none" 
					stroke="currentColor" 
					stroke-width="10" 
					stroke-linecap="round" 
					stroke-linejoin="round" 
					aria-hidden="true">
					<!-- central pivot and balance beam -->
					<circle cx="65" cy="20" r="10"/>
					<path d="M25 20 H55"/>
					<path d="M75 20 H105"/>
					<!-- path d="M65 0 V10"/ -->
					<!-- central vertical beam -->
					<path d="M65 30 V90"/>
					<!-- stand at the bottom -->
					<path d="M35 90 H90"/>
					<!-- path fill="currentColor" stroke="none" d="M25 105 V110 A10 10 0 0 1 30 95 H95 A10 10 0 0 1 105 100 V105 Z"/ -->
					<!-- left hanger and pan -->
					<path d="M25 20 L45 65 L5 65 Z"/>
					<path fill="currentColor" d="M5 65 A10 5 0 0 0 45 65 Z"/>
					<!-- right hanger and pan -->
					<path d="M105 20 L125 65 L85 65 Z"/>
					<path fill="currentColor" d="M85 65 A10 5 0 0 0 125 65 Z"/>
				</svg>
				<div class="footer-icon-title">Polls</div>
			</div>
			<!-- Same placeholder on the right, for the same reason. -->
			<div v-else class="footer-icon-container" aria-hidden="true"></div>
		</div>
	</footer>
</template>

<script>
import TeamIcon from "@/liquido/components/team-icon.vue"

/**
 * A flexible navbar component shown at the bottom of many SEAF pages.
 * Each SEAF page can have it's own footer depending on UX and context.
 * This footer has two slots for icons on the left and right, 
 * and a primary action button in the middle.
 */
export default {
	name: "LiquidoFooter",
	components: { TeamIcon },
	props: {
		infoText: { type: String, default: undefined },
		primaryText: { type: String, default: undefined },
		primaryDisabled: { type: Boolean, default: false },
		/**
		 * Hide the Team and Polls icons, keeping their space so the centered action button does not
		 * change width. For pages reached without a login - /verifyEmail is opened straight from a
		 * mail - where both destinations would just bounce the visitor to the login screen.
		 */
		hideNavIcons: { type: Boolean, default: false },
	},
	emits: ["primary"],
	data() {
		return {
			footerResizeObserver: null,
			iconSwitch: true
		}
	},
	mounted() {
	},
	methods: {
		
	},
}
</script>

<style>
/*
 * Plain position: fixed; bottom: 0 - it follows Safari's floating toolbar without lag (pwa-header-and-footer-iOS-2026).
 * The safe-area inset is padding, not margin: the footer's white fills the area under the home indicator of a home screen
 * app (34px on an iPhone), and the icons stay above it. In a browser tab the inset is 0.
 */
.liquido-footer {
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: stretch;
	flex-direction: column;

	margin: 0;
	padding: 0 0 env(safe-area-inset-bottom);
	background: var(--header-bg);
	border-top: 1px solid var(--light-border);
	box-shadow: 0 -5px 10px rgba(0, 0, 0, 0.1);
	z-index: 999;
}

.liquido-footer-info {
	width: 100%;
	flex-grow: 1;
	text-align: center;
	color: var(--liquido-info-color);
	font-size: var(--font-size-small);
	line-height: 1.2;
	color: var(--secondary);
	padding: 0.5rem;
	p:last-child {
		margin-bottom: 0;
	}
}

.liquido-footer-actions {
	display: flex;
	align-items: center;
	flex-grow: 1;
	min-width: 0;
	margin: 0;
	padding: 0;
}

/*
 * With the nav icons hidden, it is their icon+label stack that stops setting the bar's height, and
 * the footer collapses from ~56px to ~39px. Pad the BUTTON to make up the difference rather than
 * pinning a height on the footer: the height still comes from the content, it is just the button's
 * content now, so the bar keeps growing normally if a label ever wraps.
 *
 * Margin, not padding: the button keeps its normal 38px size and simply gets breathing room around
 * it, rather than growing into a chunky tall button. The bar was losing ~17px without the icons, so
 * ~8.5px above and below restores it.
 *
 * Margins here do not collapse away: .footer-center is a flex item of .liquido-footer-actions, and a
 * flex item's children never margin-collapse through it.
 */
.liquido-footer-actions.no-nav-icons .footer-center .btn {
	margin-top: 0.55rem;
	margin-bottom: 0.55rem;
}

.footer-center {
	flex: 5 1 0;
	min-width: 0;
	button {
		display: block;
		/*flex-grow: 1;*/
		width: 100%;
		min-width: 0;
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	}
}

.footer-icon-container {
	display: flex;
	flex-direction: column;
	flex: 1 1 0;
	align-items: center;
	justify-content: center;
	color: var(--primary);
	text-decoration: none;
	/* Need to manually adjust the vertical padding of the icons on the left and right, so that they look aligned with the main action button in the middle. */
	padding: 0.5rem 0 0.2rem 0rem;  
	height: 100%;
	text-decoration: none;
	transition: background-color 0.2s;
}

.footer-icon {	
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	font-size: 1.8rem;
	color: var(--primary);
	height: 100%;
}

/* Custom stroke-based SVG icons in the footer scale with the icon font-size */
.footer-svg-icon {
	width: 2em;
	height: 1.40em;
	display: block;
}


.footer-icon-title {
	font-size: 10px;
	text-decoration: none;
}

/*
.liquido-footer-actions .btn {
	flex-grow: 1;
}
	*/

</style>
